import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Save, Download, FileText, CalendarIcon, AlertCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { QuizProfile, BlockScores } from "./quizData";
import { generateQuizPDF } from "@/utils/quizPdfGenerator";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { validateCNPJ, validateEmail, getValidationMessage } from "@/utils/validators";
import { useCalendarAvailability } from "@/hooks/useCalendarAvailability";
import { useSupplierDuplicateCheck } from "@/hooks/useSupplierDuplicateCheck";

interface QuizLeadFormProps {
  profile: QuizProfile;
  totalScore: number;
  blockScores: BlockScores;
  answers: Record<number, number>;
  name: string;
  whatsapp: string;
  prospectId?: string | null;
  onSubmit: (data: LeadFormData) => void;
  onScheduleMeeting?: (prefillData: QuizPrefillData) => void;
}

export interface LeadFormData {
  email: string;
  cnpj: string;
  quizResponseId?: string;
}

export interface QuizPrefillData {
  name: string;
  phone: string;
  email: string;
  cnpj: string;
  quizResponseId: string;
}

const QuizLeadForm = ({ profile, totalScore, blockScores, answers, name, whatsapp, prospectId: existingProspectId, onSubmit, onScheduleMeeting }: QuizLeadFormProps) => {
  const [formData, setFormData] = useState<{ email: string; cnpj: string; companyName: string }>({
    email: "",
    cnpj: "",
    companyName: "",
  });
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [quizResponseId, setQuizResponseId] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, cnpj: false });
  const { availableSlots, loading: slotsLoading, fetchAvailability } = useCalendarAvailability();
  const { duplicates, checkField } = useSupplierDuplicateCheck();

  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
      setSelectedTime(""); // reset time when date changes
    }
  }, [selectedDate, fetchAvailability]);

  // Remove static timeSlots - now using dynamic availableSlots

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  const handleChange = (field: "email" | "cnpj" | "companyName", value: string) => {
    if (field === "cnpj") {
      const formatted = formatCNPJ(value);
      setFormData((prev) => ({ ...prev, cnpj: formatted }));
      const digits = value.replace(/\D/g, "");
      if (digits.length === 14) checkField("cnpj", digits);
    } else if (field === "email") {
      setFormData((prev) => ({ ...prev, email: value }));
      if (value.includes("@") && value.length > 5) checkField("email", value);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (field === "email" || field === "cnpj") {
      setTouched((t) => ({ ...t, [field]: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const scheduledDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;

      // First update prospect with lead form data via upsert
      if (existingProspectId) {
        await supabase.functions.invoke("upsert-prospect", {
          body: {
            id: existingProspectId,
            fullName: name,
            phone: whatsapp,
            email: formData.email,
            cnpj: formData.cnpj,
            companyName: formData.companyName,
            scheduledDate,
            scheduledTime: selectedTime || null,
            source: "quiz",
            quizAnswers: answers,
            quizTotalScore: totalScore,
            quizBlockScores: blockScores,
            quizProfileId: profile.id,
            quizProfileTitle: profile.title,
          },
        });
      }

      const { data, error } = await supabase.functions.invoke("save-quiz-response", {
        body: {
          name,
          whatsapp,
          email: formData.email,
          cnpj: formData.cnpj,
          companyName: formData.companyName,
          answers,
          totalScore,
          profileId: profile.id,
          profileTitle: profile.title,
          blockScores,
          scheduledDate,
          scheduledTime: selectedTime || null,
          prospectId: existingProspectId,
        },
      });

      if (error) {
        console.error("Supabase invoke error:", error);
        let message = "Erro ao conectar com o servidor. Tente novamente.";
        try {
          const context = JSON.parse(error.context?.body || "{}");
          if (context.error) message = context.error;
        } catch {
          if (error.message) message = error.message;
        }
        throw new Error(message);
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      const responseId = data?.prospectId || existingProspectId || null;
      setQuizResponseId(responseId);
      setIsSuccess(true);
      onSubmit({ email: formData.email, cnpj: formData.cnpj, quizResponseId: responseId });

      toast.success("Diagnóstico salvo com sucesso!");
    } catch (error: any) {
      console.error("Erro ao salvar quiz:", error);
      const message = error?.message || "Erro ao salvar. Tente novamente.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const pdf = await generateQuizPDF({ name, profile, totalScore, blockScores, answers });
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `diagnostico-ameup-${name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleScheduleMeeting = () => {
    if (onScheduleMeeting && quizResponseId) {
      onScheduleMeeting({
        name,
        phone: whatsapp,
        email: formData.email,
        cnpj: formData.cnpj,
        quizResponseId,
      });
    }
  };

  const isEmailValid = validateEmail(formData.email);
  const isCnpjValid = validateCNPJ(formData.cnpj);
  const emailMsg = touched.email ? getValidationMessage("email", formData.email) : null;
  const cnpjMsg = touched.cnpj ? getValidationMessage("cnpj", formData.cnpj) : null;

  const isFormValid =
    isEmailValid &&
    formData.companyName.trim().length > 0 &&
    isCnpjValid &&
    selectedDate !== undefined &&
    selectedTime !== "" &&
    !duplicates.email &&
    !duplicates.cnpj;

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12 space-y-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-lime/20"
        >
          <CheckCircle2 className="w-10 h-10 text-brand-lime" />
        </motion.div>
        
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-brand-dark">
            Obrigado pelo interesse!
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Nossa equipe entrará em contato com você em breve para avaliar como podemos apoiar seu negócio.
          </p>
        </div>

        {/* PDF Download Option */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="pt-4 space-y-4"
        >
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <FileText className="w-5 h-5" />
            <span>Deseja baixar seu diagnóstico em PDF?</span>
          </div>
          
          <Button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            variant="outline"
            className="gap-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white transition-colors"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Gerando PDF...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Baixar PDF do Diagnóstico
              </>
            )}
          </Button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-brand-dark">
          Quase lá! Preencha seus dados
        </h2>
        <p className="text-muted-foreground">
          Nossa equipe entrará em contato para apresentar as soluções ideais para o seu perfil.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
        <div className="space-y-1">
          <Label htmlFor="email" className="text-brand-dark">E-mail</Label>
          <Input
            id="email"
            type="email"
            placeholder="email@empresa.com"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            className="h-12 rounded-xl"
          />
          {emailMsg && (
            <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {emailMsg}
            </p>
          )}
          {duplicates.email && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Esse e-mail já está no nosso cadastro. Caso seja você, faça login na sua conta.
            </p>
          )}
          {touched.email && isEmailValid && !duplicates.email && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              E-mail válido
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="companyName" className="text-brand-dark">Nome da Empresa</Label>
          <Input
            id="companyName"
            type="text"
            placeholder="Ex: Buffet Delícias"
            value={formData.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
            className="h-12 rounded-xl"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="cnpj" className="text-brand-dark">CNPJ</Label>
          <Input
            id="cnpj"
            type="text"
            placeholder="00.000.000/0000-00"
            value={formData.cnpj}
            onChange={(e) => handleChange("cnpj", e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, cnpj: true }))}
            className="h-12 rounded-xl"
            maxLength={18}
          />
          {cnpjMsg && (
            <p className="flex items-center gap-1.5 text-xs text-destructive mt-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {cnpjMsg}
            </p>
          )}
          {duplicates.cnpj && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600 mt-1">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Esse CNPJ já está no nosso cadastro. Caso seja você, faça login na sua conta.
            </p>
          )}
          {touched.cnpj && isCnpjValid && !duplicates.cnpj && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 mt-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              CNPJ válido
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-brand-dark">Data da Reunião</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full h-12 rounded-xl justify-start text-left font-normal",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : "Selecione uma data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label className="text-brand-dark">Horário</Label>
          {slotsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verificando disponibilidade...
            </div>
          ) : !selectedDate ? (
            <p className="text-sm text-muted-foreground py-2">Selecione uma data primeiro</p>
          ) : availableSlots.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nenhum horário disponível nesta data. Tente outro dia.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {availableSlots.map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => setSelectedTime(time)}
                  className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                    selectedTime === time
                      ? "bg-brand-orange text-white shadow-md"
                      : "bg-accent/50 hover:bg-accent border border-transparent hover:border-border/50"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={!isFormValid || isSubmitting}
          className="w-full h-12 bg-brand-orange hover:bg-brand-orange/90 text-white rounded-xl font-semibold text-base gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Finalizando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              FINALIZAR
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
};

export default QuizLeadForm;
